const RTL_CHAR_REGEX = /[\u0590-\u08FF\uFB1D-\uFDFD\uFE70-\uFEFC]/;
const RTL_FILENAME_MARK = "\u200F";

export function containsRtlText(text: string): boolean {
  return RTL_CHAR_REGEX.test(text);
}

export function isStartOfTextRtl(text: string) {
  // Determines whether or not a text is considered RTL (i.e from a language that is written "right to left"), based on
  // the first character in the text.
  if (!text) return false; // Handle empty string
  // TODO: This hasn't been tested for all RTL languages, only for Hebrew so far.
  return RTL_CHAR_REGEX.test(text[0]);
}

/** Build a PDF filename that renders correctly in RTL save/open dialogs. */
export function invoicePdfFilename(externalId: string): string {
  const id = externalId.trim();
  if (!id) return "invoice.pdf";

  if (!containsRtlText(id)) {
    return `invoice_${id}.pdf`;
  }

  // Leading ASCII (e.g. "invoice_") before Hebrew breaks Unicode bidi in browsers.
  return `${RTL_FILENAME_MARK}${id}.pdf`;
}

export const parseStringToFloat = (value: string | null): number => {
  if (!value) return 0;
  const parsed = parseFloat(value.replace(/,/g, ""));
  return isNaN(parsed) ? 0 : parsed;
};

export const sanitizeSecretName = (name: string): string => {
  /**
   * Sanitizes a string to make it compatible with Google Cloud Secret Manager naming requirements.
   * Secret IDs can only contain letters, numbers, hyphens, and underscores.
   * Replaces any invalid characters (including spaces) with underscores.
   *
   * @param name - The string to sanitize
   * @returns Sanitized string safe for use as a secret ID
   *
   * @example
   * sanitizeSecretName("Levi Fichman") // returns "Levi_Fichman"
   * sanitizeSecretName("test@user-123") // returns "test_user-123"
   */
  return name
    .split("")
    .map((char) => {
      // Keep alphanumeric, hyphens, and underscores; replace everything else with underscore
      if (/[a-zA-Z0-9_-]/.test(char)) {
        return char;
      }
      return "_";
    })
    .join("");
};

export const exportToCSV = (data: any[], filename: string) => {
  if (!data || data.length === 0) return;

  const headers = Object.keys(data[0]);
  const csvRows = [];

  csvRows.push(headers.join(","));

  for (const row of data) {
    const values = headers.map((header) => {
      const escaped = ("" + row[header]).replace(/"/g, '""');
      return `"${escaped}"`;
    });
    csvRows.push(values.join(","));
  }

  const csvContent = csvRows.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.csv`;
  link.click();
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

export const base64ToPdfFile = (externalId: string, base64: string): File => {
  // Strip data URI prefix if present
  let cleanBase64 = base64;
  if (base64.startsWith("data:")) {
    const commaIndex = base64.indexOf(",");
    if (commaIndex !== -1) {
      cleanBase64 = base64.substring(commaIndex + 1);
    }
  }

  const byteCharacters = atob(cleanBase64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: "application/pdf" });

  const file = new File([blob], invoicePdfFilename(externalId), {
    type: "application/pdf",
    lastModified: Date.now(),
  });

  return file;
};

export const base64ToPdfBlobUrl = (externalId: string, base64: string): string => {
  const file = base64ToPdfFile(externalId, base64);
  return URL.createObjectURL(file);
};

/** Data URLs render PDFs in iframes reliably; blob URLs often show a blank viewer. */
export const base64ToPdfDataUrl = (base64: string): string => {
  if (base64.startsWith("data:")) return base64;
  return `data:application/pdf;base64,${base64}`;
};

export const extractLinksFromEmailToImage = (htmlContent: string): string => {
  /**
   * Extracts image URLs wrapped in brackets [https://logo.png] and converts them to <img> elements.
   * Email text example: "hello ... [https://logo.png] ... best regards"
   *
   * @param htmlContent - The raw HTML content from the email
   * @returns HTML string with bracketed image URLs converted to <img> tags
   */
  if (!htmlContent) return "";

  // Pattern to match [image_url] where image_url is an image URL
  // Matches: [https://example.com/logo.png] or [http://example.com/image.jpg?size=large]
  // Also handles URLs without protocol: [//example.com/logo.png] or [example.com/logo.png]
  const bracketedImagePattern =
    /\[((?:https?:\/\/)?\/?[^\s\]]+\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)(?:\?[^\]]*)?)\]/gi;

  // Find all matches
  const matches = Array.from(htmlContent.matchAll(bracketedImagePattern));

  if (matches.length === 0) {
    return htmlContent;
  }

  // Extract all links to array
  const imageLinks: string[] = matches.map((match) => {
    let url = match[1]; // e.g., "https://example.com/logo.png" or "//example.com/logo.png"

    // If URL doesn't start with http:// or https://, add https://
    if (!url.match(/^https?:\/\//i)) {
      // If it starts with //, add https:
      if (url.startsWith("//")) {
        url = "https:" + url;
      } else {
        // Otherwise, add https://
        url = "https://" + url;
      }
    }

    return url;
  });

  // Replace each match with an img tag
  let result = htmlContent;

  // Process matches in reverse order to maintain correct indices
  matches.reverse().forEach((match, idx) => {
    const fullMatch = match[0]; // e.g., "[https://example.com/logo.png]"
    const imageUrl = imageLinks[imageLinks.length - 1 - idx]; // Get corresponding processed URL

    // Create img tag (equivalent to JSX <Image src="{link}" />)
    const imgTag = `<img src="${imageUrl}" alt="Image" style="max-width: 100%; height: auto; display: block; margin: 8px 0; border-radius: 4px;" />`;

    // Replace the bracketed link with the img tag
    result =
      result.substring(0, match.index!) +
      imgTag +
      result.substring(match.index! + fullMatch.length);
  });

  return result;
};

